/* eslint-disable no-param-reassign */
import {IconButton} from '@dropbox/dig-components/buttons';
import {TextInput, TextInputRefObject} from '@dropbox/dig-components/text_fields';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, CloseLine, DeleteLine, EditLine} from '@dropbox/dig-icons/assets';
import {$isAutoLinkNode, $isLinkNode, LinkNode, TOGGLE_LINK_COMMAND} from '@lexical/link';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$isAtNodeEnd} from '@lexical/selection';
import {$findMatchingParent, mergeRegister} from '@lexical/utils';
import {derivedThemeAtom} from 'atoms/layout';
import cx from 'classnames';
import {Link} from 'components/DSYS/Link';
import {useAtomValue} from 'jotai';
import {
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  ElementNode,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
  TextNode,
} from 'lexical';
import * as React from 'react';
import {Dispatch, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {INTERNAL_URL_MATCHER, URL_MATCHER} from './AutoLinkPlugin';
import styles from './FloatingLinkEditor.module.css';
import {TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND} from './SelectedTextToolbar';

const VERTICAL_GAP = 10;
const HORIZONTAL_OFFSET = 5;
const HOVER_DELAY = 300;
const HIDE_DELAY = 150;
// Offset to position the popup above the link text
const POPUP_Y_OFFSET = 40;

const SUPPORTED_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'sms:', 'tel:']);

function sanitizeUrl(url: string): string {
  const trimmedUrl = url.trim();
  // Add http:// to URLs that start with drl/*
  if (trimmedUrl.startsWith('drl/')) {
    return `http://${trimmedUrl}`;
  }

  // Check if URL already has a protocol
  const hasProtocol = /^[a-zA-Z]+:\/\//.test(trimmedUrl);

  let correctedUrl = trimmedUrl;
  if (!hasProtocol) {
    // Prepend https:// if no protocol is provided
    correctedUrl = `https://${trimmedUrl}`;
  } else {
    // Handle the existing case where https:// is duplicated
    correctedUrl = trimmedUrl.replace(/^https:\/\/(https?:\/\/)/, '$1');
  }

  try {
    const parsedUrl = new URL(correctedUrl);
    // eslint-disable-next-line no-script-url
    if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
      return 'about:blank';
    }
  } catch {
    return correctedUrl;
  }
  return correctedUrl;
}

function isValidUrl(url: string): boolean {
  const sanitizedUrl = sanitizeUrl(url);
  return INTERNAL_URL_MATCHER.test(sanitizedUrl) || URL_MATCHER.test(sanitizedUrl);
}

function setFloatingElemPositionForLinkEditor(
  targetRect: DOMRect | null,
  floatingElem: HTMLElement,
  anchorElem: HTMLElement,
  verticalGap: number = VERTICAL_GAP,
  horizontalOffset: number = HORIZONTAL_OFFSET
): void {
  const scrollerElem = anchorElem.parentElement;

  if (targetRect === null || !scrollerElem) {
    floatingElem.style.opacity = '0';
    floatingElem.style.transform = 'translate(-10000px, -10000px)';
    return;
  }

  const floatingElemRect = floatingElem.getBoundingClientRect();
  const anchorElementRect = anchorElem.getBoundingClientRect();
  const editorScrollerRect = scrollerElem.getBoundingClientRect();

  let top = targetRect.top - verticalGap;
  let left = targetRect.left - horizontalOffset;

  if (top < editorScrollerRect.top) {
    top += floatingElemRect.height + targetRect.height + verticalGap * 2;
  }

  if (left + floatingElemRect.width > editorScrollerRect.right) {
    left = editorScrollerRect.right - floatingElemRect.width - horizontalOffset;
  }

  top -= anchorElementRect.top;
  left -= anchorElementRect.left;

  floatingElem.style.opacity = '1';
  floatingElem.style.transform = `translate(${left}px, ${top + 42}px)`;
}

function getSelectedNode(selection: RangeSelection): TextNode | ElementNode {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
  }
}

const FloatingLinkEditor = ({
  editor,
  isLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
  linkNodeKey,
  currentLinkElement,
  onClose,
  onDeleteLink,
  onMouseEnter,
  onMouseLeave,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  anchorElem: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
  linkNodeKey: string | null;
  currentLinkElement: HTMLElement | null;
  onClose: () => void;
  onDeleteLink: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<TextInputRefObject>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [originalLinkUrl, setOriginalLinkUrl] = useState('');

  // Reset toolbar when link editor is closed
  useEffect(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
    }
  }, [isLink, editor]);
  useEffect(() => {
    return () => {
      editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
    };
  }, [editor]);

  // Update link URL from the current link node
  const updateLinkUrl = useCallback(() => {
    if (!linkNodeKey) {
      setLinkUrl('');
      setOriginalLinkUrl('');
      return;
    }

    editor.getEditorState().read(() => {
      const linkNode = $getNodeByKey(linkNodeKey);
      if (linkNode && $isLinkNode(linkNode)) {
        const url = linkNode.getURL();
        setLinkUrl(url);
        setOriginalLinkUrl(url);
      }
    });
  }, [editor, linkNodeKey]);

  // Update URL when linkNodeKey changes
  useEffect(() => {
    updateLinkUrl();
  }, [updateLinkUrl]);

  // Update position based on hovered element
  useEffect(() => {
    const editorElem = editorRef.current;
    if (!editorElem || !currentLinkElement || !isLink) {
      return;
    }

    const rect = currentLinkElement.getBoundingClientRect();
    const targetRect = new DOMRect(rect.x, rect.y + POPUP_Y_OFFSET, rect.width, rect.height);
    setFloatingElemPositionForLinkEditor(targetRect, editorElem, anchorElem);
  }, [anchorElem, currentLinkElement, isLink]);

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        (event: KeyboardEvent) => {
          event.stopPropagation();
          if (isLink) {
            setIsLinkEditMode(false);
            onClose();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor, isLink, setIsLinkEditMode, onClose]);

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLinkSubmission();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setLinkUrl(originalLinkUrl);
      setIsLinkEditMode(false);
      onClose();
    }
  };

  const handleLinkSubmission = () => {
    if (!linkNodeKey) {
      return;
    }

    if (isValidUrl(linkUrl)) {
      const sanitizedUrl = sanitizeUrl(linkUrl);
      // Directly update the link node's URL
      editor.update(() => {
        const linkNode = $getNodeByKey(linkNodeKey);
        if (linkNode && $isLinkNode(linkNode)) {
          linkNode.setURL(sanitizedUrl);
        }
      });
      setLinkUrl(sanitizedUrl);
      setOriginalLinkUrl(sanitizedUrl);
      setIsLinkEditMode(false);
      onClose();
    } else {
      setLinkUrl('');
    }
  };

  const handleCancel = () => {
    // If this was a new empty link, delete it
    if (!originalLinkUrl && linkNodeKey) {
      onDeleteLink();
    } else {
      setLinkUrl(originalLinkUrl);
      setIsLinkEditMode(false);
      onClose();
    }
  };

  return (
    <ThemeProvider mode={useAtomValue(derivedThemeAtom) === 'bright' ? 'dark' : 'bright'}>
      <ThemeContainer>
        <div
          ref={editorRef}
          className={styles.editor}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {!isLink || (!isLinkEditMode && !linkUrl) ? null : isLinkEditMode ? (
            <div className={styles.view}>
              <div className={styles.link}>
                <TextInput
                  ref={inputRef}
                  className={styles.input}
                  value={linkUrl}
                  onChange={(event) => {
                    setLinkUrl(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    handleInputKeyDown(event);
                  }}
                />
              </div>

              <IconButton
                variant="borderless"
                tabIndex={0}
                onMouseDown={(event: any) => event.preventDefault()}
                onClick={handleCancel}
                aria-label="icon button"
              >
                <UIIcon src={CloseLine} />
              </IconButton>

              <IconButton
                variant="borderless"
                tabIndex={0}
                onMouseDown={(event: any) => event.preventDefault()}
                onClick={handleLinkSubmission}
                disabled={!linkUrl}
                aria-label="icon button"
              >
                <UIIcon src={CheckmarkLine} />
              </IconButton>
            </div>
          ) : (
            <div className={styles.view}>
              <Link
                className={cx(styles.link, styles.text)}
                to={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Text>
                  <Truncate maxWidth={285}>{linkUrl}</Truncate>
                </Text>
              </Link>

              <IconButton
                variant="borderless"
                tabIndex={0}
                onMouseDown={(event: any) => event.preventDefault()}
                onClick={() => {
                  setIsLinkEditMode(true);
                }}
                aria-label="icon button"
              >
                <UIIcon src={EditLine} />
              </IconButton>

              <IconButton
                variant="borderless"
                tabIndex={0}
                onMouseDown={(event: any) => event.preventDefault()}
                onClick={onDeleteLink}
                aria-label="icon button"
              >
                <UIIcon src={DeleteLine} />
              </IconButton>
            </div>
          )}
        </div>
      </ThemeContainer>
    </ThemeProvider>
  );
};

function useFloatingLinkEditorToolbar(editor: LexicalEditor, anchorElem: HTMLElement) {
  const [isLink, setIsLink] = useState(false);
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);
  const [linkNodeKey, setLinkNodeKey] = useState<string | null>(null);
  const [currentLinkElement, setCurrentLinkElement] = useState<HTMLElement | null>(null);

  // Use refs for mutable state that shouldn't trigger re-renders
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentLinkElementRef = useRef<HTMLElement | null>(null);
  const isMouseOverPopupRef = useRef(false);
  const isLinkEditModeRef = useRef(false);
  const popupContainerRef = useRef<HTMLDivElement | null>(null);
  const linkNodeKeyRef = useRef<string | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    isLinkEditModeRef.current = isLinkEditMode;
  }, [isLinkEditMode]);

  useEffect(() => {
    linkNodeKeyRef.current = linkNodeKey;
  }, [linkNodeKey]);

  // Find the nearest link element from a DOM node
  const findLinkElement = useCallback((target: HTMLElement): HTMLElement | null => {
    return target.closest('a[href]') as HTMLElement | null;
  }, []);

  // Clear all timeouts
  const clearAllTimeouts = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Hide the popup
  const hidePopup = useCallback(() => {
    // Don't hide if mouse is over popup or in edit mode
    if (isMouseOverPopupRef.current || isLinkEditModeRef.current) {
      return;
    }
    setIsLink(false);
    setIsLinkEditMode(false);
    setLinkNodeKey(null);
    setCurrentLinkElement(null);
    currentLinkElementRef.current = null;
  }, []);

  // Close the popup (used by child component, bypasses mouse/edit checks)
  const closePopup = useCallback(() => {
    clearAllTimeouts();
    setIsLink(false);
    setIsLinkEditMode(false);
    setLinkNodeKey(null);
    setCurrentLinkElement(null);
    currentLinkElementRef.current = null;
    isMouseOverPopupRef.current = false;
  }, [clearAllTimeouts]);

  // Delete the link
  const deleteLink = useCallback(() => {
    const nodeKey = linkNodeKeyRef.current;
    if (!nodeKey) {
      closePopup();
      return;
    }

    editor.update(() => {
      const linkNode = $getNodeByKey(nodeKey) as LinkNode | null;
      if (linkNode && $isLinkNode(linkNode)) {
        // Select all content within the link
        linkNode.select();

        // Now dispatch the toggle command to remove the link
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
    });

    editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
    closePopup();
  }, [editor, closePopup]);

  // Show the popup for a given link element
  const showPopupForLink = useCallback(
    (linkElement: HTMLElement) => {
      editor.read(() => {
        const node = $getNearestNodeFromDOMNode(linkElement);
        if (!node) {
          return;
        }

        const linkNode = $findMatchingParent(node, $isLinkNode);
        if (linkNode && !$isAutoLinkNode(linkNode)) {
          setLinkNodeKey(linkNode.getKey());
          setCurrentLinkElement(linkElement);
          currentLinkElementRef.current = linkElement;
          setIsLink(true);
          if (linkNode.getURL() === '') {
            setIsLinkEditMode(true);
          }
        }
      });
    },
    [editor]
  );

  const showPopupForSelectedEmptyLink = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return false;
    }

    const node = getSelectedNode(selection);
    const linkParent = $findMatchingParent(node, $isLinkNode);
    const autoLinkParent = $findMatchingParent(node, $isAutoLinkNode);

    if (linkParent !== null && autoLinkParent === null) {
      const url = linkParent.getURL();
      if (url === '') {
        const linkDomNode = editor.getElementByKey(linkParent.getKey());
        if (linkDomNode) {
          clearAllTimeouts();
          setLinkNodeKey(linkParent.getKey());
          setCurrentLinkElement(linkDomNode as HTMLElement);
          currentLinkElementRef.current = linkDomNode as HTMLElement;
          setIsLink(true);
          setIsLinkEditMode(true);
          return true;
        }
      }

      return false;
    }

    if (isLinkEditModeRef.current) {
      closePopup();
    }

    return false;
  }, [clearAllTimeouts, closePopup, editor]);

  // Handle mouse entering a link
  const handleLinkMouseEnter = useCallback(
    (linkElement: HTMLElement) => {
      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      // If already showing this link, do nothing
      if (currentLinkElementRef.current === linkElement) {
        return;
      }

      // If showing a different link, hide it first
      if (currentLinkElementRef.current) {
        clearAllTimeouts();
        setIsLink(false);
        setIsLinkEditMode(false);
        setLinkNodeKey(null);
        setCurrentLinkElement(null);
        currentLinkElementRef.current = null;
      }

      // Start hover timer
      hoverTimeoutRef.current = setTimeout(() => {
        hoverTimeoutRef.current = null;
        showPopupForLink(linkElement);
      }, HOVER_DELAY);
    },
    [clearAllTimeouts, showPopupForLink]
  );

  // Handle mouse leaving a link
  const handleLinkMouseLeave = useCallback(() => {
    // Clear hover timeout if link not yet shown
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Start hide timeout
    if (currentLinkElementRef.current) {
      hideTimeoutRef.current = setTimeout(() => {
        hideTimeoutRef.current = null;
        hidePopup();
      }, HIDE_DELAY);
    }
  }, [hidePopup]);

  // Popup mouse handlers
  const handlePopupMouseEnter = useCallback(() => {
    isMouseOverPopupRef.current = true;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handlePopupMouseLeave = useCallback(() => {
    isMouseOverPopupRef.current = false;
    // Start hide timeout
    hideTimeoutRef.current = setTimeout(() => {
      hideTimeoutRef.current = null;
      hidePopup();
    }, HIDE_DELAY);
  }, [hidePopup]);

  // Set up event listeners on editor content
  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;
    if (!scrollerElem) {
      return;
    }

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Ignore events from inside the popup
      if (popupContainerRef.current?.contains(target)) {
        // But still clear hide timeout if mouse is over popup
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        return;
      }

      const linkElement = findLinkElement(target);

      if (linkElement) {
        // Only handle if this is a new link (mouseenter behavior)
        if (currentLinkElementRef.current !== linkElement && !hoverTimeoutRef.current) {
          handleLinkMouseEnter(linkElement);
        } else if (currentLinkElementRef.current === linkElement) {
          // Mouse is still over the same link, clear any hide timeout
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
          }
        }
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      const linkElement = findLinkElement(target);

      // Only handle if leaving a link element
      if (!linkElement) {
        return;
      }

      // Check if moving to another element within the same link
      if (relatedTarget && linkElement.contains(relatedTarget)) {
        return;
      }

      // Check if moving to the popup
      if (relatedTarget && popupContainerRef.current?.contains(relatedTarget)) {
        return;
      }

      handleLinkMouseLeave();
    };

    scrollerElem.addEventListener('mouseover', handleMouseOver);
    scrollerElem.addEventListener('mouseout', handleMouseOut);

    return () => {
      scrollerElem.removeEventListener('mouseover', handleMouseOver);
      scrollerElem.removeEventListener('mouseout', handleMouseOut);
      clearAllTimeouts();
    };
  }, [anchorElem, findLinkElement, handleLinkMouseEnter, handleLinkMouseLeave, clearAllTimeouts]);

  // Listen for selection changes to detect:
  // 1. New empty links created via toolbar
  // 2. Clicking elsewhere in the doc while in edit mode
  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({editorState}) => {
        editorState.read(() => {
          showPopupForSelectedEmptyLink();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          showPopupForSelectedEmptyLink();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, showPopupForSelectedEmptyLink]);

  // Listen for clicks outside the popup to close it when in edit mode
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only handle when in edit mode
      if (!isLinkEditModeRef.current) {
        return;
      }

      const target = event.target as HTMLElement;

      // Don't close if clicking inside the popup
      if (popupContainerRef.current?.contains(target)) {
        return;
      }

      // Don't close if clicking the current link
      if (currentLinkElementRef.current?.contains(target)) {
        return;
      }

      // Close the popup
      closePopup();
    };

    // Use capture phase to catch clicks before they bubble
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [closePopup]);

  return createPortal(
    <div ref={popupContainerRef}>
      <FloatingLinkEditor
        editor={editor}
        isLink={isLink}
        anchorElem={anchorElem}
        isLinkEditMode={isLinkEditMode}
        setIsLinkEditMode={setIsLinkEditMode}
        linkNodeKey={linkNodeKey}
        currentLinkElement={currentLinkElement}
        onClose={closePopup}
        onDeleteLink={deleteLink}
        onMouseEnter={handlePopupMouseEnter}
        onMouseLeave={handlePopupMouseLeave}
      />
    </div>,
    anchorElem
  );
}

export function FloatingLinkEditorPlugin({anchorElem = document.body}: {anchorElem?: HTMLElement}) {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(editor, anchorElem);
}
